import SelectInput, {
    SelectOption,
} from '@components/common/SelectInput/SelectInput';
import DatePicker from '@components/common/DatePicker';
import TextInput from '@components/common/TextInput';
import { farmingHist, FarmlandAddReq } from '@/api/growth/farmland/type';
import { useState } from 'react';
import { formatDateString, parseDate } from '@utils/Utils';

interface ResidueMngProps {
    formData: FarmlandAddReq;
    onInputChange: (field: string, value: farmingHist[]) => void;
}

const ResidueMng = ({ formData, onInputChange }: ResidueMngProps) => {
    const [farmingHists, setFarmingHists] = useState<farmingHist[]>(
        formData.farmingHistList?.filter(h => h.category === 'R') || [
            {
                category: 'R',
                workDt: '',
                iamt: '',
                iame: '',
                chcod: '',
                chamt: '',
                chme: '',
                erad: '',
                emax: '',
                emin: '',
                ewind: '',
                ename: '',
                fmcd: '',
                facd: '',
                famn: '',
                famp: '',
                famk: '',
                rcod: '',
                ramt: '',
                rdep: '',
                timpl: '',
                tdep: '',
            },
        ],
    );

    const handleFarmingHistChange = (
        index: number,
        field: keyof farmingHist,
        value: string | number,
    ) => {
        const updated = [...farmingHists];
        updated[index] = { ...updated[index], [field]: value };
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const updateParentFarmingHistList = (residueData: farmingHist[]) => {
        const otherData =
            formData.farmingHistList?.filter(h => h.category !== 'R') || [];
        const allData = [...otherData, ...residueData];
        onInputChange('farmingHistList', allData);
    };

    const addFarmingHist = () => {
        const newRecord: farmingHist = {
            category: 'R',
            workDt: '',
            iamt: '',
            iame: '',
            chcod: '',
            chamt: '',
            chme: '',
            erad: '',
            emax: '',
            emin: '',
            ewind: '',
            ename: '',
            fmcd: '',
            facd: '',
            famn: '',
            famp: '',
            famk: '',
            rcod: '',
            ramt: '',
            rdep: '',
            timpl: '',
            tdep: '',
        };
        const updated = [...farmingHists, newRecord];
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const removeFarmingHist = (index: number) => {
        const updated = farmingHists.filter((_, i) => i !== index);
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const residueCodeOptions: SelectOption[] = [
        { value: 'RE001', label: '[일반] 일반 작물 잔사' },
        { value: 'RE101', label: '[일반] 일반 두류 잔사' },
        { value: 'RE201', label: '[일반] 일반 곡류 잔사' },
        { value: 'RE301', label: '[일반] 일반 목초' },
        { value: 'RE999', label: '[일반] 분해된 작물 잔사' },
        { value: 'RE002', label: '[유기질비료] 녹비' },
        { value: 'RE003', label: '[유기질비료] 퇴비 (가축분뇨)' },
        { value: 'RE004', label: '[유기질비료] 액비' },
        { value: 'RE005', label: '[유기질비료] 부숙 퇴비' },
        { value: 'RE006', label: '[유기질비료] 수피' },
        { value: 'RE102', label: '[두류] 동부 잔사' },
        { value: 'RE103', label: '[두류] 무쿠나(벨벳콩) 잔사' },
        { value: 'RE104', label: '[두류] 땅콩 잔사' },
        { value: 'RE105', label: '[두류] 비둘기콩 잔사' },
        { value: 'RE106', label: '[두류] 대두 잔사' },
        { value: 'RE107', label: '[두류] 알팔파 잔사' },
        { value: 'RE108', label: '[두류] 병아리콩 사료작물' },
        { value: 'RE109', label: '[두류] 잠두 잔사' },
        { value: 'RE110', label: '[두류] 완두 잔사' },
        { value: 'RE111', label: '[두류] 헤어리베치 잔사' },
        { value: 'RE202', label: '[곡류] 진주조 잔사' },
        { value: 'RE203', label: '[곡류] 옥수수 잔사' },
        { value: 'RE204', label: '[곡류] 수수 잔사' },
        { value: 'RE205', label: '[곡류] 밀 잔사' },
        { value: 'RE206', label: '[곡류] 보리 잔사' },
        { value: 'RE207', label: '[곡류] 벼 잔사 (볏짚)' },
        { value: 'RE208', label: '[곡류] 호밀 잔사' },
        { value: 'RE302', label: '[목초] 바히아그라스 잔사' },
        { value: 'RE303', label: '[목초] 버뮤다그라스 잔사' },
        { value: 'RE304', label: '[목초] 스위치그라스 잔사' },
        { value: 'RE305', label: '[목초] 브라키아리아 잔사' },
        { value: 'RE306', label: '[목초] 사료용 목초 잔사' },
        { value: 'RE401', label: '[기타] 관목 휴경지 잔사' },
        { value: 'RE402', label: '[기타] 사탕수수 잔사' },
        { value: 'RE403', label: '[기타] 파인애플 잔사' },
        { value: 'RE404', label: '[기타] 카사바 잔사' },
    ];

    return (
        <div className="residue-management">
            <div className="row mb-3">
                <div className="col-12 text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addFarmingHist}
                    >
                        <i className="ri-add-line me-1"></i>
                        유기질비료 추가
                    </button>
                </div>
            </div>

            {farmingHists.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-3">
                        <thead className="text-center">
                            <tr>
                                <th style={{ width: '20%' }}>일자 선택</th>
                                <th style={{ width: '40%' }}>처리방법</th>
                                <th style={{ width: '15%' }}>
                                    처리량
                                    <div className="fs-9">(kg/ha)</div>
                                </th>
                                <th style={{ width: '15%' }}>
                                    혼합깊이
                                    <div className="fs-9">(cm)</div>
                                </th>
                                <th style={{ width: '10%' }}>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farmingHists.map((record, index) => (
                                <tr key={index}>
                                    <td>
                                        <DatePicker
                                            selectedDate={parseDate(
                                                record.workDt || '',
                                            )}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'workDt',
                                                    formatDateString(value),
                                                )
                                            }
                                            size="sm"
                                            placeholder="날짜를 선택해주세요"
                                            maxDate={undefined}
                                            showYearDropdown={false}
                                            showMonthDropdown={false}
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={residueCodeOptions}
                                            value={record.rcod || ''}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'rcod',
                                                    value,
                                                )
                                            }
                                            placeholder="처리방법을 선택해주세요"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.ramt
                                                    ? record.ramt.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'ramt',
                                                    value,
                                                )
                                            }
                                            placeholder="처리량 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={false}
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.rdep
                                                    ? record.rdep.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'rdep',
                                                    value,
                                                )
                                            }
                                            placeholder="깊이 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={false}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                removeFarmingHist(index)
                                            }
                                            title="삭제"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {farmingHists.length === 0 && (
                <div className="alert alert-info text-center py-4">
                    <p className="mb-0">
                        <i className="ri-information-line me-2"></i>
                        유기질비료(R) 관련 내용을 입력해주세요. 추가 버튼을 눌러
                        유기질비료 기록을 추가할 수 있습니다.
                    </p>
                </div>
            )}
        </div>
    );
};

export default ResidueMng;
